import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { useTranslation } from 'react-i18next';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallContentBox } from '../../ToolCallContentBox';
import { ToolCallDataList } from '../../ToolCallDataList';
import { ToolCallError } from '../../ToolCallError';
import { ToolCallState, useToolCallParser } from '../../useToolCallParser';

interface FieldDiscoverTool {
  name: AgentTools.sf_field_discover;
  args: { objectName: string } | { objectNameArray: string[] };
}

export type FieldDiscoverToolCall = ToolCall<FieldDiscoverTool['name'], FieldDiscoverTool['args']>;

export type FieldDiscoverToolCallSummary = ToolCallSummary<
  FieldDiscoverTool['name'],
  FieldDiscoverTool['args']
>;

interface FieldDiscoverResult {
  [key: string]: unknown;
}

const MAX_OBJECT_NAMES_DISPLAY = 2;

export function FieldDiscoverToolCall({ toolCall }: { toolCall: FieldDiscoverToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<FieldDiscoverResult>(
    toolCall,
    ({ toolCallState }) => {
      const objectNamesArray =
        'objectNameArray' in toolCall.args
          ? toolCall.args.objectNameArray
          : [toolCall.args.objectName];
      const isExceeded = objectNamesArray.length > MAX_OBJECT_NAMES_DISPLAY;
      const objectNames = objectNamesArray
        .slice(0, MAX_OBJECT_NAMES_DISPLAY)
        .map(name => `'${name}'`)
        .join(', ');
      switch (toolCallState) {
        case ToolCallState.Running:
          return t(
            isExceeded
              ? 'toolCalls.sf_field_discover.title.whileRunningWithMore'
              : 'toolCalls.sf_field_discover.title.whileRunning',
            { objectNames }
          );
        case ToolCallState.Error:
          return t(
            isExceeded
              ? 'toolCalls.sf_field_discover.title.errorsWithMore'
              : 'toolCalls.sf_field_discover.title.errors',
            { objectNames }
          );
        case ToolCallState.Success:
          return t(
            isExceeded
              ? 'toolCalls.sf_field_discover.title.successWithMore'
              : 'toolCalls.sf_field_discover.title.success',
            { objectNames }
          );
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallContentBox content={toolCall.results} />
          )
        ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.sf_field_discover.parameters.objectNames'),
              value:
                'objectNameArray' in toolCall.args
                  ? toolCall.args.objectNameArray.join(', ')
                  : toolCall.args.objectName,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
